// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import Layout from './components/Layout';
import LegalWatermark from './components/LegalWatermark';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import ZonesPage from './pages/ZonesPage';
import CleaningSessionPage from './pages/CleaningSessionPage';
import IncidentReportPage from './pages/IncidentReportPage';
import SupervisorQueuePage from './pages/SupervisorQueuePage';
import ComplianceDashboardPage from './pages/ComplianceDashboardPage';

// Roles that can approve sessions
const SUPERVISOR_ROLES = ['supervisor', 'manager', 'owner', 'admin'];

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function SupervisorRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (!SUPERVISOR_ROLES.includes(user.role)) return <Navigate to="/" />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (SUPERVISOR_ROLES.includes(user.role)) return <Navigate to="/compliance" />;
  return <Navigate to="/zones" />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPasswordPage onBack={() => window.location.href = '/login'} />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<HomeRedirect />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="zones" element={<ZonesPage />} />
        <Route path="zones/:zoneId/session" element={<CleaningSessionPage />} />
        <Route path="incidents" element={<IncidentReportPage />} />
        <Route path="supervisor-queue" element={<SupervisorRoute><SupervisorQueuePage /></SupervisorRoute>} />
        <Route path="compliance" element={<SupervisorRoute><ComplianceDashboardPage /></SupervisorRoute>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LegalWatermark appName="AisleSync" />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
